/**
 * ShipAddressEditForm.js — Edit a single saved shipping address (kind
 * "checkout-address-edit-form"). Common Commerce7 checkout step, opened by
 * the pencil icon on ShipAddressOptions.
 *
 * Parity with old CHECKOUT.cart.editShippingForm (RedConcierge/static/
 * javascript/main.js ~5444), simplified like the rest of this port's ship
 * forms: state/country are text inputs rather than a country→region
 * dropdown fetch (see CheckoutForms.js's documented simplification), and
 * there's no email field — a saved C7 address has no email of its own.
 *
 * message.data: { address: {...}, updateEvent?, errorScope? }
 *
 * updateEvent/errorScope default to the cart-checkout names below; club_c7.js
 * passes its own so this form's update doesn't also fire checkout_c7.js's
 * cart-checkout listener (no session guard of its own).
 *
 * EVENTS OUT: ws-checkout-address-update { addressId, payload } (default)
 * Listens for ws-checkout-error (scope === errorScope) to re-enable Save on
 * a failed update.
 */

import { on, emit } from "../../../core/state.js";

function field(label, name, { required = false, placeholder = "", value = "" } = {}) {
  const group = document.createElement("div");
  group.className = "ws-checkout-field";
  const lab = document.createElement("label");
  lab.textContent = label;
  group.appendChild(lab);
  const input = document.createElement("input");
  input.type = "text";
  input.name = name;
  input.className = "ws-checkout-input";
  input.placeholder = placeholder || label;
  if (value) input.value = value;
  if (required) input.required = true;
  group.appendChild(input);
  const err = document.createElement("small");
  err.className = "ws-checkout-error";
  err.dataset.errorField = name;
  group.appendChild(err);
  return { group, input, err };
}

export default class ShipAddressEditForm {
  render(message) {
    const addr = message?.data?.address || {};
    const updateEvent = message?.data?.updateEvent || "ws-checkout-address-update";
    const errorScope = message?.data?.errorScope || "address";

    const form = document.createElement("form");
    form.className = "ws-checkout-form ws-ship-address-edit-form";
    form.setAttribute("action", "javascript:void(0);");
    form.noValidate = true;

    const sec = document.createElement("div");
    sec.className = "ws-checkout-section";
    const head = document.createElement("div");
    head.className = "ws-checkout-section-head";
    const title = document.createElement("p");
    title.className = "ws-checkout-section-title";
    title.textContent = "Edit shipping address";
    head.appendChild(title);
    sec.appendChild(head);

    const fields = document.createElement("div");
    fields.className = "ws-checkout-fields";
    sec.appendChild(fields);

    const refs = {};
    const add = (label, name, opts) => {
      const { group, input, err } = field(label, name, opts);
      refs[name] = { input, err };
      fields.appendChild(group);
    };

    add("First Name", "firstName", { required: true, value: addr.firstName });
    add("Last Name", "lastName", { required: true, value: addr.lastName });
    add("Company (optional)", "company", { value: addr.company });
    add("Address", "address", { required: true, value: addr.address });
    add("Apartment, suite, etc. (optional)", "address2", { value: addr.address2 });
    add("City", "city", { required: true, value: addr.city });
    add("State", "stateCode", { required: true, value: addr.stateCode });
    add("Postal Code", "zipCode", { required: true, value: addr.zipCode });
    add("Country", "countryCode", { required: true, value: addr.countryCode || "US" });
    add("Phone", "phone", { required: true, placeholder: "+12025550100", value: addr.phone });

    const defaultRow = document.createElement("label");
    defaultRow.className = "ws-checkout-field ws-ship-address-default-row";
    const defaultCheck = document.createElement("input");
    defaultCheck.type = "checkbox";
    defaultCheck.checked = !!addr.isDefault;
    defaultRow.appendChild(defaultCheck);
    const defaultLabel = document.createElement("span");
    defaultLabel.textContent = "Make my default address";
    defaultRow.appendChild(defaultLabel);
    fields.appendChild(defaultRow);

    const submitErr = document.createElement("small");
    submitErr.className = "ws-checkout-error ws-checkout-submit-error";
    fields.appendChild(submitErr);

    form.appendChild(sec);

    const footer = document.createElement("div");
    footer.className = "ws-checkout-footer";
    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "ws-checkout-submit";
    submit.textContent = "Update";
    footer.appendChild(submit);
    sec.appendChild(footer);

    let pending = false;

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      Object.values(refs).forEach(({ err }) => (err.textContent = ""));
      submitErr.textContent = "";

      const missing = Object.entries(refs).find(([, ref]) => ref.input.required && !ref.input.value.trim());
      if (missing) {
        missing[1].err.textContent = "Required";
        return;
      }

      const payload = {
        firstName: refs.firstName.input.value.trim(),
        lastName: refs.lastName.input.value.trim(),
        company: refs.company.input.value.trim(),
        address: refs.address.input.value.trim(),
        address2: refs.address2.input.value.trim(),
        city: refs.city.input.value.trim(),
        stateCode: refs.stateCode.input.value.trim(),
        zipCode: refs.zipCode.input.value.trim(),
        countryCode: refs.countryCode.input.value.trim(),
        phone: refs.phone.input.value.trim(),
        isDefault: defaultCheck.checked,
      };

      pending = true;
      submit.disabled = true;
      emit(updateEvent, { addressId: addr.id, payload });
    });

    on("ws-checkout-error", ({ scope, message: msg } = {}) => {
      if (!form.isConnected || !pending || scope !== errorScope) return;
      pending = false;
      submit.disabled = false;
      submitErr.textContent = msg || "We couldn't update that address. Please try again.";
    });

    return form;
  }
}
